import { Component, EventEmitter, Input, Output, inject } from '@angular/core';
import { Router } from '@angular/router';
import {
	AdAccountListItem,
	MetaLimits,
	SyncWarning
} from '@brand-lolly/app/_models/brand-instagram-integration.model';
import { AdAccountsService } from '@brand-lolly/app/services/meta-integrations/ad-accounts.service';
import { extractMetaErrorMessage } from '@brand-lolly/app/shared/utils/meta-api-error.utils';
import { LoDialogService, LoNotice, LoToastService } from 'lo-design-system';
import { Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';
import {
	MetaAdAccountModalComponent,
	MetaAdAccountModalData
} from './meta-ad-account-modal.component';
import { MetaAdAccountRowComponent } from './meta-ad-account-row.component';
import {
	MetaAdReleaseBlockedData,
	MetaAdReleaseBlockedDialogComponent,
	MetaAdReleaseBlockedResult
} from './meta-ad-release-blocked-dialog.component';
import { MetaLimitsStripComponent } from './meta-limits-strip.component';
import { MetaSectionComponent } from './meta-section.component';

const CAMPAIGNS_DEEP_LINK = '/partner-ads';

@Component({
	standalone: true,
	selector: 'meta-ad-accounts-section',
	imports: [
		LoNotice,
		MetaSectionComponent,
		MetaAdAccountRowComponent,
		MetaLimitsStripComponent
	],
	template: `
		<meta-section
			heading="Ad accounts"
			icon="facebook_logo"
			[count]="countLabel"
			[loading]="loading"
			[skeletonRows]="4"
		>
			@if (!loading && limits) {
			<meta-limits-strip
				slot="header-extra"
				[primary]="adPrimary"
				[caption]="adCaption"
				[consumed]="limits.ad_account.currently_consumed"
				[max]="limits.ad_account.plan_max"
			></meta-limits-strip>
			} @for (warn of syncWarnings; track warn.connection_id + ':' +
			warn.code) {
			<lo-notice
				tone="warning"
				[heading]="
					warn.message + ' (connection #' + warn.connection_id + ')'
				"
			></lo-notice>
			} @for (ad of accounts; track ad.id) {
			<meta-ad-account-row
				[account]="ad"
				[remainingSlots]="limits?.ad_account?.remaining_slots ?? null"
				[toggleLoading]="loadingIds.has(ad.id)"
				(toggle)="onToggle($event)"
				(manage)="onManage($event)"
			></meta-ad-account-row>
			} @if (!accounts.length) {
			<p class="empty lo-type-text-md">No ad accounts discovered.</p>
			}
		</meta-section>
	`,
	styles: [
		`
			:host {
				display: block;

				.empty {
					margin: 0;
					color: var(--lo-color-text-secondary);
					text-align: center;
				}
			}
		`
	]
})
export class MetaAdAccountsSectionComponent {
	@Input() accounts: AdAccountListItem[] = [];
	@Input() limits: MetaLimits | null = null;
	@Input() syncWarnings: SyncWarning[] = [];
	@Input() loading = false;

	@Output() refresh = new EventEmitter<void>();

	loadingIds = new Set<string>();

	private destroy$ = new Subject<void>();

	private readonly dialogs = inject(LoDialogService);

	constructor(
		private service: AdAccountsService,
		private toasts: LoToastService,
		private router: Router
	) {}

	get countLabel(): string {
		const discovered =
			this.limits?.ad_account?.discovered ?? this.accounts.length;
		return `${discovered} discovered`;
	}

	get adPrimary(): string {
		const ad = this.limits?.ad_account;
		if (!ad) return '';
		return `${ad.currently_consumed} of ${ad.plan_max} active`;
	}

	get adCaption(): string {
		const ad = this.limits?.ad_account;
		if (!ad) return '';
		if (ad.inactive > 0) return `${ad.inactive} inactive`;
		return '';
	}

	onManage(account: AdAccountListItem): void {
		this.dialogs
			.open<
				MetaAdAccountModalComponent,
				MetaAdAccountModalData,
				{ refreshed?: boolean }
			>(MetaAdAccountModalComponent, { data: { account } })
			.closed.subscribe(result => {
				if (result?.refreshed) this.refresh.emit();
			});
	}

	onToggle(account: AdAccountListItem): void {
		if (this.loadingIds.has(account.id)) return;
		if (account.is_consumed) {
			this.dispatchRelease(account);
			return;
		}
		this.dispatchConsume(account);
	}

	private dispatchConsume(account: AdAccountListItem): void {
		this.markLoading(account.id, true);
		this.service
			.consume(account.id)
			.pipe(takeUntil(this.destroy$))
			.subscribe({
				next: updated => {
					this.applyUpdate(updated);
					this.markLoading(account.id, false);
				},
				error: err => {
					this.markLoading(account.id, false);
					if (err?.status !== 402) {
						this.toasts.error(
							extractMetaErrorMessage(
								err,
								'Failed to activate ad account'
							)
						);
					}
				}
			});
	}

	private dispatchRelease(account: AdAccountListItem): void {
		this.markLoading(account.id, true);
		this.service
			.release(account.id)
			.pipe(takeUntil(this.destroy$))
			.subscribe({
				next: updated => {
					this.applyUpdate(updated);
					this.markLoading(account.id, false);
				},
				error: err => {
					this.markLoading(account.id, false);
					if (err?.status === 409) {
						this.handleReleaseBlocked(account, err);
					} else {
						this.toasts.error(
							extractMetaErrorMessage(
								err,
								'Failed to release ad account'
							)
						);
					}
				}
			});
	}

	private handleReleaseBlocked(
		account: AdAccountListItem,
		err: {
			error?: {
				error?: string | null;
				message?: string;
				active_campaigns_count?: number;
				detail?: {
					message?: string;
					active_campaigns_count?: number;
				};
			};
		}
	): void {
		const body = err.error;
		const detail = body?.detail;
		const activeCampaignsCount =
			body?.active_campaigns_count ??
			detail?.active_campaigns_count ??
			account.campaigns_count;
		const message =
			(typeof body?.error === 'string' && body.error) ||
			body?.message ||
			detail?.message ||
			'Cannot release ad account while active campaigns reference it.';
		this.dialogs
			.open<
				MetaAdReleaseBlockedDialogComponent,
				MetaAdReleaseBlockedData,
				MetaAdReleaseBlockedResult
			>(MetaAdReleaseBlockedDialogComponent, {
				data: {
					adAccountId: account.id,
					adAccountName: account.name,
					activeCampaignsCount,
					message
				}
			})
			.closed.subscribe(res => {
				if (res?.navigate) {
					this.router.navigateByUrl(CAMPAIGNS_DEEP_LINK);
				}
			});
	}

	private applyUpdate(updated: AdAccountListItem): void {
		const idx = this.accounts.findIndex(a => a.id === updated.id);
		if (idx === -1) return;
		this.accounts = [
			...this.accounts.slice(0, idx),
			updated,
			...this.accounts.slice(idx + 1)
		];
		if (this.limits) {
			const consumed = this.accounts.filter(a => a.is_consumed).length;
			const ad = this.limits.ad_account;
			this.limits = {
				...this.limits,
				ad_account: {
					...ad,
					currently_consumed: consumed,
					remaining_slots:
						ad.plan_max === null
							? null
							: Math.max(0, ad.plan_max - consumed)
				}
			};
		}
	}

	private markLoading(id: string, on: boolean): void {
		const next = new Set(this.loadingIds);
		if (on) next.add(id);
		else next.delete(id);
		this.loadingIds = next;
	}
}
